import { ComponentChildren } from 'preact';
import { stringLiteralArray } from '../utils/UNSAFE_arrayUtils';
import { Flex } from '../UNSAFE_Flex';
import { WindowOverlay, WindowOverlayPlacement } from '../UNSAFE_WindowOverlay';
import { useUser } from '../hooks/UNSAFE_useUser';
import { Layer } from '../UNSAFE_Layer';

const positions = stringLiteralArray([
  'top',
  'top-left',
  'top-right',
  'top-start',
  'top-end',
  'bottom',
  'bottom-left',
  'bottom-right',
  'bottom-start',
  'bottom-end'
]);

type Position = typeof positions[number];

type Offset =
  | number
  | {
      horizontal?: number;
      vertical?: number;
    };

type Props = {
  children: ComponentChildren;

  /**
   * Defines the fixed position where the notifications will to be placed on the screen.
   * - top-start resolves to top-left in LTR (top-right in RTL)
   * - top-end resolves to top-right in LTR (top-left in RTL)
   * - bottom-start resolves to bottom-left in LTR (bottom-right in RTL)
   * - bottom-end resolves to bottom-right in LTR (bottom-left in RTL)
   *
   * default bottom
   */
  position?: Position;

  /**
   * Defines an offset (in pixels) in the placement. Value can be supplied as a number or
   * an object with horizontal and vertical values.
   *
   * default 0
   */
  offset?: Offset;

  /**
   * The test id which is assigned to the "data-testid" for the component's element.
   * Used for test automation, it is assigned to the wrapper element rendered by the component.
   */
  testId?: string;
};

const MessageLayer = ({ children, offset, testId, position = 'bottom' }: Props) => {
  const side = position.split('-')[0];
  let windowOverlayOffset;
  if (typeof offset === 'number') {
    windowOverlayOffset = { mainAxis: offset, crossAxis: offset };
  } else if (side == 'top' || side == 'bottom') {
    windowOverlayOffset = { mainAxis: offset?.vertical, crossAxis: offset?.horizontal };
  } else {
    windowOverlayOffset = { mainAxis: offset?.horizontal, crossAxis: offset?.vertical };
  }

  const { direction } = useUser();
  const isLtr = direction === 'ltr';
  let windowOverlayPlacement: WindowOverlayPlacement | undefined;
  switch (position) {
    case 'top-left':
      windowOverlayPlacement = isLtr ? 'top-start' : 'top-end';
      break;
    case 'top-right':
      windowOverlayPlacement = isLtr ? 'top-end' : 'top-start';
      break;
    case 'bottom-left':
      windowOverlayPlacement = isLtr ? 'bottom-start' : 'bottom-end';
      break;
    case 'bottom-right':
      windowOverlayPlacement = isLtr ? 'bottom-end' : 'bottom-start';
      break;
    default:
      windowOverlayPlacement = position;
      break;
  }
  return (
    <Layer>
      <WindowOverlay placement={windowOverlayPlacement} offset={windowOverlayOffset}>
        <div data-testid={testId}>
          <Flex direction="column" gap="2x">
            {children}
          </Flex>
        </div>
      </WindowOverlay>
    </Layer>
  );
};

export type { Position, Offset };
export { MessageLayer, positions };
